import { useState } from 'react';

import { Button, Col, Form, Input, Modal, Row, Select, Typography } from 'antd';
import { Divider } from 'antd';
import axios from 'axios';

import MainLayout from '@/components/Layout/MainLayout';
import { stateOptions } from '@/config';
import useGlobalStore from '@/store/global';

const { Title, Text } = Typography;
const { Option } = Select;

const ProviderEnrollmentForm = () => {
  const { themeColor } = useGlobalStore();
  const [form] = Form.useForm();
  const [signupLoading, setSignupLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const [resendEmailLoading, setResendEmailLoading] = useState(false);
  const email = form.getFieldValue('email');

  const onFinish = async (values: any) => {
    try {
      setSignupLoading(true);
      await axios.post('provider_users', values);
      setModalOpen(true);
    } catch (error) {
      console.log(error);
    } finally {
      setSignupLoading(false);
    }
  };

  const handleResendEmail = async () => {
    const email = form.getFieldValue('email');
    try {
      setResendEmailLoading(true);
      await axios.get('provider_users/resent_confirmation_email', {
        params: { email },
      });
    } catch (error) {
      console.log(error);
    } finally {
      setResendEmailLoading(false);
    }
  };

  const addressFields = [
    'reimbursement_address',
    'reimbursement_address_line2',
    'reimbursement_address_city',
    'reimbursement_address_state',
    'reimbursement_address_zip',
  ];

  // Rule for validating fields when any address field has a value
  const getAddressFieldRule = () => ({
    validator: async (_: any, value: any) => {
      // Get all form values
      const allValues = form.getFieldsValue();

      // Check if any address field has a value
      const hasAnyValue = addressFields.some(
        (field) =>
          allValues[field] !== undefined && allValues[field] !== null && allValues[field] !== ''
      );

      console.log('hasAnyValue: ', hasAnyValue);

      // If any field has a value and current field is empty, throw error
      if (hasAnyValue && (!value || value === '')) {
        throw new Error('This field is required when any address field is filled');
      }

      return Promise.resolve();
    },
  });

  return (
    <MainLayout>
      <Modal centered open={modalOpen} onCancel={() => setModalOpen(false)} footer={false}>
        <Title level={3} style={{ color: themeColor }}>
          Thank you for signing up!
        </Title>
        <Text>
          You've entered <b>{email}</b> as your email address for you account. Please check your
          email and click the confirmation link to complete your registration.
        </Text>
        <div className="mt-6 gap-2">
          <Divider />
          If you have not received any mail,{' '}
          <button
            className="text-blue-500 hover:underline cursor-pointer"
            disabled={resendEmailLoading}
            onClick={handleResendEmail}
          >
            Resend confirmation email
          </button>
        </div>
      </Modal>
      <div className="mx-auto md:px-10">
        <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: themeColor }}>
          PROVIDER ENROLLMENT FORM
        </h2>
        <hr className="p-2" />
        <Text style={{ fontWeight: 500 }}>
          <b>Specialty Pharmacies or Healthcare Providers</b>: Enroll here to assist your eligible
          patients with their copays.
        </Text>

        <div>
          <Form form={form} onFinish={onFinish} layout="vertical">
            <Row className="mt-6" gutter={{ xs: 16, sm: 20, md: 24 }}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item name="business_name" label="Business Name" rules={[{ required: true }]}>
                  <Input placeholder="Financial reimbursement will be issued to this name" />
                </Form.Item>
              </Col>
            </Row>

            <div className="py-5">
              <h3 className="text-xl md:text-2xl font-bold mb-4">SECTION 1: PROVIDER LOCATION</h3>
              <Text>
                THIS SECTION IS MANDATORY. IT REQUIRES THE ADDRESS AND SPECIFIC INFORMATION OF YOUR
                PROVIDER.
              </Text>
            </div>

            <Row gutter={{ xs: 16, sm: 20, md: 24 }}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item name="setting" label="Location Setting" rules={[{ required: true }]}>
                  <Select placeholder="Setting of this provider location">
                    <Option value={1} key="1">
                      Physician Infusion Center
                    </Option>
                    <Option value={2} key="2">
                      Hospital Outpatient Facility
                    </Option>
                    <Option value={4} key="4">
                      Specialty Pharmacy (Home Infusion)
                    </Option>
                    <Option value={8} key="8">
                      Specialty Pharmacy
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="business_address"
                  label="Location Address"
                  rules={[{ required: true }]}
                >
                  <Input placeholder="Location street address" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item name="business_address_line2" label="Location address Line 2">
                  <Input placeholder="Location street address line 2" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item name="business_city" label="Location city" rules={[{ required: true }]}>
                  <Input placeholder="Location city" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="business_state"
                  label="Location State"
                  rules={[{ required: true }]}
                >
                  <Select placeholder="Location state">
                    {stateOptions.map((option) => (
                      <Option value={option.value} key={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item name="business_zip" label="Location ZIP" rules={[{ required: true }]}>
                  <Input placeholder="Location ZIP code (5-digits e.g. 91101)" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="business_phone"
                  label="Location phone"
                  rules={[{ required: true }]}
                >
                  <Input placeholder="Location phone (including area code)" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item name="business_fax" label="Location fax" rules={[{ required: true }]}>
                  <Input placeholder="Location fax number" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="point_of_contact"
                  label="Location contact person name"
                  rules={[{ required: true }]}
                >
                  <Input placeholder="First and last name of the billing contact at this location" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item name="tax_id" label="Location tax ID" rules={[{ required: true }]}>
                  <Input placeholder="Please provide the tax ID for this location" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item name="provider_npi" label="Provider NPI" rules={[{ required: true }]}>
                  <Input placeholder="Please provide the 10-digit NPI number for this location" />
                </Form.Item>
              </Col>
            </Row>

            <div className="py-5">
              <h3 className="text-xl md:text-2xl font-bold mb-4">
                SECTION 2: ALTERNATE FINANCIAL REIMBURSEMENT ADDRESS
              </h3>
              <Text>
                THIS SECTION IS OPTIONAL. IT IS ONLY REQUIRED IF YOUR PROVIDER LOCATION (ADDRESS
                LISTED ABOVE) WANTS TO RE-DIRECT COPAY ASSISTANCE REIMBURSEMENT TO A DIFFERENT
                ADDRESS. IF LEFT BLANK, REIMBURSEMENT WILL BE SENT TO THE ADDRESS LISTED IN SECTION
                1 ABOVE.
              </Text>
            </div>

            <Row gutter={{ xs: 16, sm: 20, md: 24 }}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="reimbursement_address"
                  label="Financial reimbursement address"
                  validateTrigger={['onChange', 'onBlur', 'onSubmit']}
                  rules={[getAddressFieldRule()]}
                >
                  <Input placeholder="Street address where financial reimbursement would be sent" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="reimbursement_address_line2"
                  label="Financial reimbursement address line 2"
                  validateTrigger={['onChange', 'onBlur', 'onSubmit']}
                  rules={[getAddressFieldRule()]}
                >
                  <Input placeholder="Financial reimbursement address line 2" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="reimbursement_address_city"
                  label="Financial reimbursement city"
                  validateTrigger={['onChange', 'onBlur', 'onSubmit']}
                  rules={[getAddressFieldRule()]}
                >
                  <Input placeholder="Financial reimbursement city" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="reimbursement_address_state"
                  label="Financial reimbursement state"
                  validateTrigger={['onChange', 'onBlur', 'onSubmit']}
                  rules={[getAddressFieldRule()]}
                >
                  <Select allowClear placeholder="Financial reimbursement state">
                    {stateOptions.map((option) => (
                      <Option value={option.value} key={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="reimbursement_address_zip"
                  label="Financial reimbursement ZIP"
                  validateTrigger={['onChange', 'onBlur', 'onSubmit']}
                  rules={[getAddressFieldRule()]}
                >
                  <Input placeholder="Financial reimbursement ZIP code (5-digits e.g. 91101)" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="reimbursement_contact_name"
                  label="Financial reimbursement contact person name"
                >
                  <Input placeholder="First and last name of the financial reimbursement contact" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="reimbursement_contact_phone"
                  label="Financial reimbursement contact person phone"
                >
                  <Input placeholder="Financial reimbursement contact direct phone (including area code)" />
                </Form.Item>
              </Col>
            </Row>

            <div className="py-5">
              <h3 className="text-xl md:text-2xl font-bold mb-4">SECTION 3: CREATE YOUR ACCOUNT</h3>
              <Text>
                THIS SECTION IS MANDATORY. THE E-MAIL ADDRESS AND THE PASSWORD YOU CREATE HERE WILL
                ENABLE YOU TO LOG INTO THIS PORTAL.
              </Text>
            </div>

            <Row gutter={{ xs: 16, sm: 20, md: 24 }}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="email"
                  label="Your email"
                  rules={[{ required: true, type: 'email' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="password"
                  label="Create a password"
                  rules={[{ required: true, min: 8 }]}
                >
                  <Input.Password placeholder="At least 8 characters long" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="password_confirmation"
                  label="Confirm created password"
                  dependencies={['password']}
                  rules={[
                    { required: true },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('The two passwords do not match'));
                      },
                    }),
                  ]}
                >
                  <Input.Password />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24}>
                <Form.Item className="md:w-1/2 mx-auto mt-6 md:mt-10">
                  <Button
                    disabled={signupLoading}
                    loading={signupLoading}
                    type="primary"
                    htmlType="submit"
                    className="w-full mx-auto h-10 text-lg"
                    style={{
                      backgroundColor: themeColor,
                    }}
                  >
                    Submit
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
    </MainLayout>
  );
};

export default ProviderEnrollmentForm;
